import { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { TaskDetail, type TaskDetailData } from '../components/task/TaskDetail';
import { RemindMeButton } from '../components/RemindMeButton';

/* ── Main Component ───────────────────────────────────── */

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const confirm = useConfirm();
  const navState = location.state as { from?: string; fromLabel?: string } | null;

  const [saving, setSaving] = useState(false);
  const [task, setTask] = useState<TaskDetailData | null>(null);

  /* ── Delete ─────────────────────────────────────────── */

  async function handleDelete() {
    if (!taskId) return;
    if (!await confirm({ title: 'Delete this task?', message: 'This cannot be undone.', variant: 'danger', confirmLabel: 'Delete' })) return;
    try {
      await apiDelete(`/tasks/${taskId}`);
      toast.success('Task deleted');
      navigate('/tasks');
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error('Failed to delete task');
    }
  }

  /* ── Render ─────────────────────────────────────────── */

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back nav */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link
              to={navState?.from ?? '/tasks'}
              className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
            >
              <ChevronLeft size={16} /> {navState?.fromLabel ?? 'Back to Tasks'}
            </Link>
            {navState?.from && navState.from !== '/tasks' && (
              <Link
                to="/tasks"
                className="text-xs text-text-muted hover:text-text-secondary transition-colors"
              >
                All Tasks
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2">
            {saving && <span className="text-xs text-accent">Saving...</span>}
            {task && <RemindMeButton entityType="task" entityId={taskId!} entityTitle={task.title} />}
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-danger hover:bg-bg-hover rounded transition-colors"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>

        <TaskDetail taskId={taskId as string} onSavingChange={setSaving} onTaskLoaded={setTask} />
      </div>
    </div>
  );
}
